/**
 * Generic debounced-autosave controller (ticket #880: description autosave,
 * no explicit save button).
 *
 * Framework-agnostic and dependency-free so it is unit-testable without
 * mounting any component — the caller (a component) only wires `update()` to
 * onChange, `flush()` to blur / close / task-switch, and reads `getStatus()`
 * to render a status indicator.
 *
 * Guarantees:
 * - Debounce: rapid update() calls collapse into a single save of the LAST
 *   value once `debounceMs` has passed without a further update().
 * - Race safety: only one save() call is ever in flight at a time. A
 *   monotonic generation counter tracks the latest value vs. the value
 *   currently being persisted; if update() lands while a save is in flight,
 *   the in-flight save's result is still applied, but a follow-up save of the
 *   newer value is queued immediately (no extra debounce wait) so the LAST
 *   typed value always wins and is never silently dropped.
 * - No data loss on error: a failed save leaves the value dirty (never
 *   reverted) and reports status "error" via onStatusChange. The next
 *   update() or an explicit flush() retries automatically.
 */

export type AutosaveStatus = "idle" | "dirty" | "saving" | "saved" | "error";

export interface AutosaveOptions<T> {
  /** Persist `value`. Rejecting leaves the controller dirty for a retry. */
  save: (value: T) => Promise<void>;
  /** Debounce delay after the last update() before an automatic save fires. */
  debounceMs?: number;
  /** Notified on every status transition (including the error itself). */
  onStatusChange?: (status: AutosaveStatus, error: unknown) => void;
}

export interface AutosaveController<T> {
  /** Report a new value (e.g. on every keystroke). (re)starts the debounce timer. */
  update(value: T): void;
  /**
   * Save the current value immediately, bypassing the debounce timer.
   * Always resolves (never rejects) — check getStatus()/getError() for the
   * outcome. Safe to call when nothing is dirty (resolves right away).
   */
  flush(): Promise<void>;
  /**
   * Re-baseline the controller onto a new starting value without saving
   * (e.g. after switching to a different record). Only call this once any
   * prior flush() has settled — calling it while a save is in flight would
   * abandon tracking of that save's outcome.
   */
  reset(value: T): void;
  getStatus(): AutosaveStatus;
  getError(): unknown;
  getValue(): T;
  isDirty(): boolean;
}

export function createAutosave<T>(
  initialValue: T,
  opts: AutosaveOptions<T>
): AutosaveController<T> {
  const { save, debounceMs = 1000, onStatusChange } = opts;

  let value = initialValue;
  let generation = 0;
  let savedGeneration = 0;
  let status: AutosaveStatus = "idle";
  let lastError: unknown = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let activeRun: Promise<void> | null = null;

  const setStatus = (next: AutosaveStatus, error: unknown = null): void => {
    status = next;
    lastError = next === "error" ? error : null;
    onStatusChange?.(status, lastError);
  };

  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // Saves the current `value` in a loop: if newer input arrives while the
  // save is in flight (generation advances past what we captured), loop
  // around and save again immediately — this is the race-safety guarantee.
  // Stops (without throwing) on the first error so the value stays dirty for
  // a later retry.
  const runLoop = async (): Promise<void> => {
    for (;;) {
      if (generation === savedGeneration) return;
      const myGeneration = generation;
      const myValue = value;
      setStatus("saving");
      try {
        await save(myValue);
      } catch (err) {
        setStatus("error", err);
        return;
      }
      if (myGeneration === generation) {
        savedGeneration = myGeneration;
        setStatus("saved");
        return;
      }
      // Newer value arrived mid-flight — loop and save it right away.
    }
  };

  const ensureRunning = (): Promise<void> => {
    if (!activeRun) {
      activeRun = runLoop().finally(() => {
        activeRun = null;
      });
    }
    return activeRun;
  };

  const update = (next: T): void => {
    value = next;
    generation += 1;
    if (status !== "saving") {
      setStatus("dirty");
    }
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void ensureRunning();
    }, debounceMs);
  };

  const flush = (): Promise<void> => {
    clearTimer();
    return ensureRunning();
  };

  const reset = (next: T): void => {
    clearTimer();
    value = next;
    generation = 0;
    savedGeneration = 0;
    activeRun = null;
    setStatus("idle");
  };

  return {
    update,
    flush,
    reset,
    getStatus: () => status,
    getError: () => lastError,
    getValue: () => value,
    isDirty: () => generation !== savedGeneration,
  };
}
